import _ from 'lodash';

import {
  cleanObjectOfEmptyValues,
  prettyType,
  replaceParams,
  unflatten,
} from 'app/common/CommonUtils';

const isValidTypeOf = (template, index) => {
  // Okay to have no type_of, because we'll set it.
  if (!template.type_of) {
    return true;
  }
  return (
    (index === 0 && template.type_of === 'read') ||
    (index > 0 && template.type_of !== 'read' && prettyType(template.type_of))
  );
};

/*
  Take query parameters like:
    > template__0__selected_api=TrelloAPI&template__1__selected_api=TrelloAPI
    > steps__1__params__key=value
    > steps__1__meta__parammap__key=label
  and build an array of node templates.
 */
export const nodeTemplatesFromQuery = (
  query = {},
  { allowSparseNodes = false } = {}
) => {
  const unflatQuery = unflatten(query);
  const instructions = _.merge(unflatQuery.template, unflatQuery.steps);

  const nodeTemplates = Object.keys(instructions)
    .filter(key => !isNaN(parseInt(key)))
    .reduce((result, key) => {
      result[key] = instructions[key];
      return result;
    }, []);

  // Must have at least one node.
  if (nodeTemplates.length === 0) {
    return null;
  }

  // Let's limit this to 50 nodes for now.
  if (nodeTemplates.length > 50) {
    throw new Error('Templates cannot have more than 50 steps.');
  }

  if (
    !allowSparseNodes &&
    _.compact(nodeTemplates).length !== nodeTemplates.length
  ) {
    throw new Error('Templates cannot have missing steps.');
  }

  return nodeTemplates.map((template, index) => {
    if (!isValidTypeOf(template, index)) {
      throw new Error(`Invalid type_of for node at index ${index}.`);
    }
    const safeTemplate = _.pick(template, [
      'title',
      'type_of',
      'selected_api',
      'action',
      'params',
      'meta',
    ]);

    safeTemplate.meta = _.pick(safeTemplate.meta || {}, ['parammap']);

    const defaultTypeOf = template.type_of || (index === 0 ? 'read' : 'write');
    return {
      ...safeTemplate,
      type_of: defaultTypeOf,
    };
  });
};

export const toZapTemplate = nodeTemplates => {
  return {
    title: nodeTemplates[0].title,
    steps: nodeTemplates.map(node => ({
      ...node,
      type: 'node',
    })),
  };
};

export const getFormaticFields = (fields, matchProperties) => {
  const matchFields = !matchProperties
    ? fields
    : _.filter(fields, matchProperties);

  return _.chain(matchFields)
    .map(field => (field.isGroup ? field.fields : field))
    .flatten()
    .compact()
    .filter(field => field.key && field.type !== 'copy')
    .value();
};

export const toReadOnlyFormatic = (fields, data, matchProperties) => {
  data = replaceParams(data, () => '');
  data = cleanObjectOfEmptyValues(data);

  const allFields = getFormaticFields(fields, matchProperties);

  if (allFields.length === 0) {
    return [];
  }

  const fieldsInfo = allFields.reduce(
    (info, field) => {
      if (field.key in data || field.placeholder) {
        info.valid.push(field);
      } else if (field.required) {
        info.emptyRequired.push(field);
      } else {
        info.emptyOptional.push(field);
      }
      return info;
    },
    {
      valid: [],
      emptyRequired: [],
      emptyOptional: [],
    }
  );

  const allFieldsWithMessage = fieldsInfo.valid.slice(0);

  if (fieldsInfo.emptyRequired.length > 0) {
    allFieldsWithMessage.push({
      type: 'empty-fields-message',
      label: 'Required fields without a value',
      fields: fieldsInfo.emptyRequired,
      required: true,
    });
  }

  if (fieldsInfo.emptyOptional.length > 0) {
    allFieldsWithMessage.push({
      type: 'empty-fields-message',
      label: 'Optional fields without a value',
      fields: fieldsInfo.emptyOptional,
    });
  }

  return allFieldsWithMessage;
};
